<template>
  <div class="supplier-wallet">
    <div class="wallet-header">
      <h1>Mon Portefeuille</h1>
      <p class="subtitle">
        Gérez vos paiements et revenus
      </p>
    </div>

    <!-- Two Main Balance Cards -->
    <div class="balance-cards">
      <!-- Incoming Payments (Pending) -->
      <div class="balance-card incoming">
        <div class="card-icon">
          <i class="fas fa-arrow-down" />
        </div>
        <div class="card-content">
          <h3>Paiements Entrants</h3>
          <p class="card-description">
            Montants des paiements validés en attente de facturation
          </p>
          <div class="amount">
            {{ formatAmount(wallet.pending_balance) }} <span class="currency">DT</span>
          </div>
          <div class="status-badge pending">
            <i class="fas fa-clock" />
            En Attente de Facturation
          </div>
        </div>
      </div>

      <!-- Received from Invoices (Confirmed) -->
      <div class="balance-card received">
        <div class="card-icon">
          <i class="fas fa-check-circle" />
        </div>
        <div class="card-content">
          <h3>Montants Reçus</h3>
          <p class="card-description">
            Montants confirmés après validation des factures
          </p>
          <div class="amount">
            {{ formatAmount(wallet.confirmed_balance) }} <span class="currency">DT</span>
          </div>
          <div class="status-badge confirmed">
            <i class="fas fa-file-invoice-dollar" />
            Facturé et Confirmé
          </div>
        </div>
      </div>
    </div>

    <!-- Transaction History -->
    <div class="transactions-section">
      <div class="section-header">
        <h2>Historique des Transactions</h2>
        <div class="filter-buttons">
          <button
            :class="{ active: statusFilter === '' }"
            @click="filterTransactions('')"
          >
            Toutes
          </button>
          <button
            :class="{ active: statusFilter === 'pending' }"
            @click="filterTransactions('pending')"
          >
            Entrants
          </button>
          <button
            :class="{ active: statusFilter === 'confirmed' }"
            @click="filterTransactions('confirmed')"
          >
            Reçus
          </button>
        </div>
      </div>

      <!-- Transactions List -->
      <div
        v-if="loading"
        class="loading"
      >
        <div class="spinner" />
        Chargement...
      </div>

      <div
        v-else-if="transactions.length > 0"
        class="transactions-list"
      >
        <div
          v-for="transaction in transactions"
          :key="transaction.id"
          class="transaction-item"
          :class="transaction.status"
        >
          <div class="tx-icon">
            <i
              :class="transaction.status === 'pending' ? 'fas fa-arrow-down' : 'fas fa-check-circle'"
            />
          </div>
          <div class="tx-info">
            <div class="tx-reference">
              {{ transaction.reference }}
            </div>
            <div class="tx-date">
              {{ formatDate(transaction.created_at) }}
            </div>
            <div
              v-if="transaction.status === 'confirmed' && transaction.confirmed_at"
              class="tx-confirmed-date"
            >
              Confirmé le {{ formatDate(transaction.confirmed_at) }}
            </div>
          </div>
          <div class="tx-amount">
            <div class="amount-value">
              {{ formatAmount(transaction.amount) }} DT
            </div>
            <div
              class="tx-status"
              :class="transaction.status"
            >
              {{ transaction.status === 'pending' ? 'Entrant' : 'Reçu' }}
            </div>
          </div>
        </div>
      </div>

      <div
        v-else
        class="empty-state"
      >
        <i class="fas fa-inbox" />
        <p>Aucune transaction disponible</p>
      </div>

      <!-- Load More -->
      <div
        v-if="hasMore && !loading"
        class="load-more"
      >
        <button
          class="btn-load-more"
          @click="loadMore"
        >
          Charger plus
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/libs/axios'

export default {
  name: 'SupplierWallet',

  data() {
    return {
      wallet: {
        pending_balance: 0,
        confirmed_balance: 0,
      },
      transactions: [],
      statusFilter: '',
      loading: false,
      page: 1,
      hasMore: true,
    }
  },

  mounted() {
    this.loadWallet()
    this.loadTransactions()
  },

  methods: {
    async loadWallet() {
      try {
        const response = await axios.get('/api/wallets/my-wallet/')
        this.wallet = response.data
      } catch (error) {
        this.$toast({
          component: this.$options.components.ToastificationContent,
          props: {
            title: 'Erreur',
            text: 'Impossible de charger les données du portefeuille',
            variant: 'danger',
            icon: 'AlertCircleIcon',
          },
        })
      }
    },

    async loadTransactions() {
      this.loading = true
      this.page = 1
      try {
        const params = {
          page: this.page,
          page_size: 20,
        }
        if (this.statusFilter) params.status = this.statusFilter

        const response = await axios.get('/api/my-wallet/transactions/', { params })
        this.transactions = response.data.results || response.data
        this.hasMore = !!response.data.next
      } catch (error) {
        this.$toast({
          component: this.$options.components.ToastificationContent,
          props: {
            title: 'Erreur',
            text: 'Impossible de charger les transactions',
            variant: 'danger',
            icon: 'AlertCircleIcon',
          },
        })
      } finally {
        this.loading = false
      }
    },

    async loadMore() {
      this.loading = true
      this.page += 1
      try {
        const params = {
          page: this.page,
          page_size: 20,
        }
        if (this.statusFilter) params.status = this.statusFilter

        const response = await axios.get('/api/my-wallet/transactions/', { params })
        const newTransactions = response.data.results || response.data
        this.transactions = [...this.transactions, ...newTransactions]
        this.hasMore = !!response.data.next
      } catch (error) {
        // ignore error
      } finally {
        this.loading = false
      }
    },

    filterTransactions(status) {
      this.statusFilter = status
      this.loadTransactions()
    },

    formatAmount(amount) {
      if (!amount) return '0,000'
      return parseFloat(amount).toFixed(3).replace('.', ',')
    },

    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.supplier-wallet {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

.wallet-header {
  margin-bottom: 2rem;

  h1 {
    font-size: 2rem;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 0.5rem;
  }

  .subtitle {
    color: #6c757d;
    font-size: 1rem;
  }
}

.balance-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
}

.balance-card {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  gap: 1.5rem;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }

  .card-icon {
    width: 60px;
    height: 60px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.8rem;
    flex-shrink: 0;
  }

  &.incoming {
    border-left: 4px solid #ff9f43;

    .card-icon {
      background: linear-gradient(135deg, #ff9f43 0%, #ffb976 100%);
      color: white;
    }
  }

  &.received {
    border-left: 4px solid #28c76f;

    .card-icon {
      background: linear-gradient(135deg, #28c76f 0%, #48da89 100%);
      color: white;
    }
  }

  .card-content {
    flex: 1;

    h3 {
      font-size: 1.25rem;
      font-weight: 600;
      color: #2c3e50;
      margin-bottom: 0.5rem;
    }

    .card-description {
      font-size: 0.875rem;
      color: #6c757d;
      margin-bottom: 1.5rem;
    }

    .amount {
      font-size: 2.5rem;
      font-weight: 700;
      color: #2c3e50;
      margin-bottom: 1rem;

      .currency {
        font-size: 1.5rem;
        font-weight: 500;
        color: #6c757d;
      }
    }

    .status-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 1rem;
      border-radius: 20px;
      font-size: 0.875rem;
      font-weight: 500;

      &.pending {
        background: #fff3e0;
        color: #ff9f43;
      }

      &.confirmed {
        background: #e8f8f0;
        color: #28c76f;
      }
    }
  }
}

.transactions-section {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;

  h2 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #2c3e50;
  }

  .filter-buttons {
    display: flex;
    gap: 0.5rem;

    button {
      padding: 0.5rem 1rem;
      border: 1px solid #e0e0e0;
      background: white;
      border-radius: 8px;
      cursor: pointer;
      font-size: 0.875rem;
      transition: all 0.2s;

      &:hover {
        background: #f5f5f5;
      }

      &.active {
        background: #7367f0;
        color: white;
        border-color: #7367f0;
      }
    }
  }
}

.loading {
  text-align: center;
  padding: 3rem;
  color: #6c757d;

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #f3f3f3;
    border-top: 3px solid #7367f0;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 1rem;
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.transactions-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.transaction-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  transition: all 0.2s;

  &:hover {
    background: #f9f9f9;
    border-color: #d0d0d0;
  }

  &.pending {
    border-left: 3px solid #ff9f43;
  }

  &.confirmed {
    border-left: 3px solid #28c76f;
  }

  .tx-icon {
    width: 45px;
    height: 45px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    flex-shrink: 0;
  }

  &.pending .tx-icon {
    background: #fff3e0;
    color: #ff9f43;
  }

  &.confirmed .tx-icon {
    background: #e8f8f0;
    color: #28c76f;
  }

  .tx-info {
    flex: 1;

    .tx-reference {
      font-weight: 600;
      color: #2c3e50;
      margin-bottom: 0.25rem;
    }

    .tx-date {
      font-size: 0.875rem;
      color: #6c757d;
    }

    .tx-confirmed-date {
      font-size: 0.8rem;
      color: #28c76f;
      margin-top: 0.25rem;
    }
  }

  .tx-amount {
    text-align: right;

    .amount-value {
      font-size: 1.25rem;
      font-weight: 600;
      color: #2c3e50;
      margin-bottom: 0.25rem;
    }

    .tx-status {
      font-size: 0.75rem;
      font-weight: 500;
      padding: 0.25rem 0.75rem;
      border-radius: 12px;
      display: inline-block;

      &.pending {
        background: #fff3e0;
        color: #ff9f43;
      }

      &.confirmed {
        background: #e8f8f0;
        color: #28c76f;
      }
    }
  }
}

.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  color: #6c757d;

  i {
    font-size: 4rem;
    margin-bottom: 1rem;
    opacity: 0.3;
  }

  p {
    font-size: 1.125rem;
  }
}

.load-more {
  text-align: center;
  margin-top: 2rem;

  .btn-load-more {
    padding: 0.75rem 2rem;
    background: #7367f0;
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.2s;

    &:hover {
      background: #5e50ee;
      transform: translateY(-2px);
    }
  }
}

@media (max-width: 768px) {
  .supplier-wallet {
    padding: 1rem;
  }

  .balance-cards {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .balance-card {
    flex-direction: column;
    text-align: center;

    .card-icon {
      margin: 0 auto;
    }

    .amount {
      font-size: 2rem;
    }
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .transaction-item {
    flex-wrap: wrap;

    .tx-amount {
      width: 100%;
      text-align: left;
      margin-top: 0.5rem;
    }
  }
}
</style>
